import React, { useState, useRef } from "react";
import { gql, useMutation } from "@apollo/client";

import { useSearchParams } from "react-router-dom";
import StepWizard from "react-step-wizard";
import { Formik, Form } from "formik";

import { ApplicationComplete } from "../ApplicationComplete/index";

import { VerifyForm } from "../../forms/Verify/VerifyForm";
import { ApplicationForm } from "../../forms/Application/ApplicationForm";
import { ReviewQuoteForm } from "../../forms/ReviewQuote/ReviewQuoteForm";
import { PaymentForm } from "../../forms/Payment/PaymentForm";
import { FinalReview } from "../../forms/FinalReview/FinalReview";

import { ManualReview } from "../../forms/Eligibility/ManualReview";
import { declineKindFor } from "../../forms/Payment/paymentFailures";
import usePaymentFailures from "../../hooks/usePaymentFailures";

import validationSchema from "../../forms/validationSchema";
import formModel from "../../forms/formModel";
import formInitialValues from "../../forms/formInitialValues";

import { Headline as VerifyHeadline } from "../../forms/Verify/VerifyForm";
import { Headline as ApplicationHeadline } from "../../forms/Application/ApplicationForm";
import { Headline as ReviewQuoteHeadline } from "../../forms/ReviewQuote/ReviewQuoteForm";
import { Headline as PaymentHeadline } from "../../forms/Payment/PaymentHeadline";
import { Headline as FinalHeadline } from "../../forms/FinalReview/FinalReview";

import { RightCol, Col, Container } from "../../styled";
import { IASessionSteps, gqlErrors } from "../../constants";

import "./wizard.css";

const { formField } = formModel;

// The card on file, unless its issuer has refused it for good (lost, stolen,
// expired...). Offering that card again only sends it back to the issuer for
// another decline, so the payment step asks for a new card instead.
const usablePaymentMethod = (contractor) =>
  contractor?.paymentMethodUnusable ? null : contractor?.paymentMethod;

function _renderHeadline(step, organizationName, entityName, data) {
  const session = data.insuranceApplicationSession;
  const isGeneralOptIn = session?.isGeneralOptIn === true;
  const quotes = session?.quotes?.edges?.[0]?.node;
  
  switch (step) {
    case 1:
      return (
        <VerifyHeadline
          organizationName={organizationName}
          entityName={entityName}
          quotes={quotes}
          isGeneralOptIn={isGeneralOptIn}
          generalOptInWorkState={session?.generalOptInWorkState}
        />
      );
    case 2:
      return <ApplicationHeadline />;
    case 3:
      return (
        <ReviewQuoteHeadline 
          organizationName={organizationName}
          withholdPremium={session?.contractor?.withholdPremium}
          withholdPremiumOrgOverride={session?.contractor?.withholdPremiumOrgOverride}
          jobWithholdPremium={session?.job?.withholdPremium}
          isGeneralOptIn={isGeneralOptIn}
        />
      );
    case 4:
      return <PaymentHeadline organizationName={organizationName} isGeneralOptIn={isGeneralOptIn} />;
    case 5:
      return <FinalHeadline 
        withholdPremium={session?.contractor?.withholdPremium}
        withholdPremiumOrgOverride={session?.contractor?.withholdPremiumOrgOverride}
        jobWithholdPremium={session?.job?.withholdPremium}
      />;
    default:
      return <div>Not Found</div>;
  }
}

const Wizard = (props) => {
  const { data, isSandbox, iaSession } = props;

  const sessionCurrentStep = data.insuranceApplicationSession?.currentStep;

  // "Orphaned" session: the backend marked it APPLICATION_COMPLETE but no
  // policy was ever bound (the terminal transition happened via the old
  // saveAndStepForward path instead of bindInsurancePolicy). This is an
  // authoritative discriminator: for a non-opt-in session,
  // currentStep === APPLICATION_COMPLETE always implies a committed policy
  // association in that same transaction, so its absence means the session is
  // orphaned. (General opt-in legitimately completes with no policy.)
  // Rather than dropping the contractor on Verify Info (step 1) with a dead
  // Continue button, we route them to FINAL_REVIEW to remediate: clicking
  // "Complete Application" binds the policy (bind has no completion lockout and
  // will bind an orphan), producing a real policy and the genuine completion
  // screen.
  const isOrphanedComplete =
    sessionCurrentStep === "APPLICATION_COMPLETE" &&
    !(data.insuranceApplicationSession?.policies?.edges?.length > 0) &&
    data.insuranceApplicationSession?.isGeneralOptIn !== true;

  // If currentStep is APPLICATION_COMPLETE (and a policy exists), we render
  // ApplicationComplete before the wizard, so the step order doesn't matter.
  // For an orphaned session, land on FINAL_REVIEW so the contractor can bind.
  // Default to step 1 otherwise.
  const currentStep = isOrphanedComplete
    ? IASessionSteps.FINAL_REVIEW.order
    : sessionCurrentStep === "APPLICATION_COMPLETE"
    ? 1 // Default value, won't be used since ApplicationComplete renders first
    : IASessionSteps[sessionCurrentStep]?.["order"] || 1;

  const currentValidationSchema = validationSchema[0];
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeStep, setActiveStep] = useState(currentStep);
  // Non-fatal error surfaced to the user when an apply-flow mutation fails.
  // Without this the "Continue" button would silently no-op on failure.
  const [stepError, setStepError] = useState(null);
  const [eligible, setEligible] = useState(
    data.insuranceApplicationSession?.isEligible
  );

  // Refetch the session so the render logic re-routes off fresh data (e.g. a
  // concurrent completion in another tab). If a policy now exists we land on
  // the completion screen; if it's orphaned we land on FINAL_REVIEW. Apollo
  // does not run a mutation's refetchQueries when that mutation errors, so we
  // trigger it explicitly from onError.
  const refetchSession = () => {
    try {
      props.client?.refetchQueries({
        include: ["InsuranceApplicationSession"],
      });
    } catch (e) {
      // noop — never let a refetch failure crash the handler
    }
  };

  const { paymentError, declineCount, reportPaymentFailure } =
    usePaymentFailures(iaSession);
  const [state, updateState] = useState({
    form: {},
    transitions: {
      enterRight: "animated enterRight",
      enterLeft: "animated enterLeft",
      exitRight: "animated exitRight",
      exitLeft: "animated exitLeft",
      intro: "animated intro",
    },
  });

  const SAVE_AND_STEP_BACK = gql`
    mutation SaveAndStepBack($currentStep: String!, $iaSession: String!) {
      saveAndStepBack(currentStep: $currentStep, iaSession: $iaSession) {
        ok
        activeStep
      }
    }
  `;

  const SAVE_AND_STEP_FORWARD = gql`
    mutation SaveAndStepForward(
      $currentStep: String!
      $iaSession: String!
      $formValues: ContractorInput!
    ) {
      saveAndStepForward(
        currentStep: $currentStep
        iaSession: $iaSession
        formValues: $formValues
      ) {
        ok
        activeStep
        embeddedSigningUrl
        esignDocumentHash
        esignProvider
        message
      }
    }
  `;

  const ref = useRef(null);

  // Dedupes concurrent/duplicate saveAndStepForward calls (e.g. the e-sign
  // auto-advance firing at almost the same moment as a manual "Continue"
  // click). Claimed synchronously at the top of stepForward and released the
  // instant the mutation settles or client-side validation fails—so it can
  // never strand the button, unlike a latch keyed off the derived loading flag.
  const stepForwardInFlightRef = useRef(false);

  // const updateForm = (key, value) => {
  //   const { form } = state;

  //   form[key] = value;
  //   updateState({
  //     ...state,
  //     form,
  //   });
  // };

  async function _submitForm(values, actions) {
    console.log("submit form", values, actions);
    // await _sleep(1000);
    // alert(JSON.stringify(values, null, 2));
    // actions.setSubmitting(false);

    // setActiveStep(activeStep + 1);
  }

  function _handleSubmit(values, actions) {
    console.log("handle submit", values, actions);
    // if (isLastStep) {
    //   _submitForm(values, actions);
    // } else {
    //   setActiveStep(activeStep + 1);
    //   actions.setTouched({});
    //   actions.setSubmitting(false);
    // }
  }

  function _handleBack() {
    console.log("handle back");
    // setActiveStep(activeStep - 1);
  }

  // Helper function to scroll to top, especially on mobile
  const scrollToTop = () => {
    // Use a small delay to ensure the DOM has updated after step transition
    setTimeout(() => {
      // Scroll to top with smooth behavior
      // Note: We only use window.scrollTo to avoid canceling the smooth animation
      // Direct scrollTop assignments would interrupt the smooth scroll
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 150); // Slightly longer delay to ensure step transition animation has started
  };

  // Maps a named step to its display order. Use this to set `activeStep` (which
  // drives the headline on the left) right after goToNamedStep: under React 18
  // batching, the wizard's own currentStep getter hasn't committed yet, so
  // reading it would leave the headline one step behind the content.
  const stepOrderFor = (name) => IASessionSteps[name]?.order;

  const [saveAndStepBack, { loading: stepBackLoading }] = useMutation(
    SAVE_AND_STEP_BACK,
    {
      onCompleted: (resp) => {
        if (resp?.saveAndStepBack?.ok) {
          setStepError(null);
          const backStep = resp?.saveAndStepBack?.activeStep;
          state.stepWizard.goToNamedStep(backStep);
          setActiveStep(stepOrderFor(backStep) || state.stepWizard.currentStep);

          // Scroll to top when navigating to eligibility page on mobile
          if (backStep === "APPLICATION_QUESTIONS") {
            scrollToTop();
          }
        }
      },
      onError: (error) => {
        const gqlError = error.graphQLErrors?.[0];
        const status = gqlError?.extensions?.status;
        if (status === gqlErrors.APPLICATION_ALREADY_COMPLETED) {
          // Terminal session—refetch (completion screen if a policy exists) and
          // route an orphaned session to FINAL_REVIEW for remediation rather
          // than leaving it stranded on an earlier step. Skip navigation for
          // general opt-in, which has no FINAL_REVIEW step.
          refetchSession();
          if (!isGeneralOptIn && state.stepWizard?.goToNamedStep) {
            state.stepWizard.goToNamedStep(IASessionSteps.FINAL_REVIEW.name);
            setActiveStep(IASessionSteps.FINAL_REVIEW.order);
          }
          return;
        }
        setStepError(
          gqlError?.message ||
            "Something went wrong. Please try again."
        );
      },
    }
  );

  const [esignEmbedUrl, setEsignEmbedUrl] = useState(
    data.insuranceApplicationSession?.embeddedSigningUrl
  );
  const [esignDocumentHash, setEsignDocumentHash] = useState(
    data.insuranceApplicationSession?.esignDocumentHash
  );
  const [esignProvider, setEsignProvider] = useState(
    data.insuranceApplicationSession?.esignProvider || "eversign"
  );

  const [
    saveAndStepForward,
    { loading: stepForwardLoading, reset: stepForwardReset },
  ] = useMutation(SAVE_AND_STEP_FORWARD, {
    onCompleted: (resp) => {
      // The mutation settled—release the in-flight guard so the next legitimate
      // advance (or a retry) can proceed.
      stepForwardInFlightRef.current = false;
      if (resp?.saveAndStepForward?.ok) {
        // Clear any previously surfaced error once a step succeeds.
        setStepError(null);
        const nextStep = resp?.saveAndStepForward?.activeStep;

        // For APPLICATION_COMPLETE, the refetch will show ApplicationComplete component
        // No need to navigate to a step in the wizard
        if (nextStep !== "APPLICATION_COMPLETE") {
          state.stepWizard.goToNamedStep(nextStep);
        }

        if (nextStep === "APPLICATION_QUESTIONS") {
          setEsignEmbedUrl(resp?.saveAndStepForward.embeddedSigningUrl);
          setEsignDocumentHash(resp?.saveAndStepForward.esignDocumentHash);
          setEsignProvider(resp?.saveAndStepForward.esignProvider || "eversign");  // Track provider
          // Scroll to top when navigating to eligibility page on mobile
          scrollToTop();
        }

        if (nextStep === "FINAL_REVIEW") {
          // Scroll to top when navigating to final review page on mobile
          scrollToTop();
        }

        // Don't move the headline for APPLICATION_COMPLETE: there's no wizard
        // step (or headline case) for it—its order (6) would render "Not Found".
        // Leave activeStep on the prior step until the refetch swaps in the
        // ApplicationComplete screen.
        if (nextStep !== "APPLICATION_COMPLETE") {
          setActiveStep(stepOrderFor(nextStep) || state.stepWizard.currentStep);
        }
      } else {
        // it shouldn't hit this, but just in case
        if (resp?.saveAndStepForward?.activeStep === "APPLICATION_COMPLETE") {
          // Post-completion lockout: a completed session rejects further
          // saveAndStepForward calls with ok:false. This is a terminal state,
          // not an eligibility failure—let the refetched session render
          // ApplicationComplete rather than dropping into ManualReview.
        } else if (["INVALID_STATE"].includes(resp?.saveAndStepForward?.message)) {
          alert(resp?.saveAndStepForward?.message);
        } else {
          setEligible(false);
        }
      }
    },
    onError: (error) => {
      // The mutation settled (with an error)—release the in-flight guard so the
      // user can retry.
      stepForwardInFlightRef.current = false;
      stepForwardReset();
      const gqlError = error.graphQLErrors?.[0];
      const status = gqlError?.extensions?.status;
      const message = gqlError?.message;

      switch (status) {
        case gqlErrors.PAYMENT_NOT_AUTHORIZED:
          // The issuer declined the authorization, or the API refused to
          // retry a card it hard-declined before; `message` is Checkout's
          // response summary (e.g. "Insufficient Funds").
          reportPaymentFailure(declineKindFor(gqlError?.extensions), message);
          // Pick up paymentMethodUnusable now, so a card the issuer just
          // refused for good stops being offered without a reload.
          refetchSession();
          return;

        case gqlErrors.APPLICATION_ALREADY_COMPLETED:
          // Post-completion lockout: the session is terminal. Refetch so a
          // genuinely-bound session resolves to the completion screen, AND move
          // the wizard to FINAL_REVIEW now so an orphaned session lands on the
          // remediation (bind) UI instead of being stranded on an earlier step.
          // (A genuinely-completed session is replaced by ApplicationComplete
          // once the refetch resolves, so this navigation is a no-op there.)
          // General opt-in has no FINAL_REVIEW step and completes legitimately,
          // so skip navigation for it.
          refetchSession();
          if (!isGeneralOptIn && state.stepWizard?.goToNamedStep) {
            state.stepWizard.goToNamedStep(IASessionSteps.FINAL_REVIEW.name);
            setActiveStep(IASessionSteps.FINAL_REVIEW.order);
          }
          return;

        case gqlErrors.POLICY_BIND_REQUIRED:
          // saveAndStepForward refused to finish the application from
          // FINAL_REVIEW—the policy must be bound via bindInsurancePolicy. The
          // FINAL_REVIEW step already wires "Complete Application" to that
          // mutation, so this is a defensive backstop: make sure the user is on
          // FINAL_REVIEW (where the bind button lives) rather than no-opping.
          if (state.stepWizard?.goToNamedStep) {
            state.stepWizard.goToNamedStep(IASessionSteps.FINAL_REVIEW.name);
            setActiveStep(IASessionSteps.FINAL_REVIEW.order);
          }
          return;

        case gqlErrors.CONTRACTOR_BLOCKED_FOR_WRITES:
          setStepError(
            message ||
              "This application can't be edited right now. Please contact support."
          );
          return;

        default:
          // Stop swallowing failures: surface something actionable instead of a
          // silent no-op button.
          setStepError(
            message ||
              "Something went wrong saving your application. Please try again."
          );
          return;
      }
    },
    refetchQueries: ["InsuranceApplicationSession"],
  });

  function filterObject(obj, callback) {
    return Object.fromEntries(
      Object.entries(obj).filter(([key, val]) => callback(val, key))
    );
  }

  function getCurrentStep() {
    // Safety check: if stepWizard is not initialized yet, return a default
    if (!state.stepWizard || !state.stepWizard.currentStep) {
      console.warn('StepWizard not initialized, using activeStep fallback');
      // Find step by activeStep
      const step = filterObject(IASessionSteps, (val, key) => {
        return val["order"] === activeStep;
      });
      return Object.keys(step)[0] || "ESIGNATURE_DOCUMENT";
    }
    
    const step = filterObject(IASessionSteps, (val, key) => {
      return val["order"] === state.stepWizard.currentStep;
    });

    return Object.keys(step)[0];
  }

  function stepBack() {
    const namedStep = getCurrentStep();

    saveAndStepBack({
      variables: {
        currentStep: namedStep,
        iaSession: iaSession,
      },
    });
  }

  function stepForward(setErrors, setTouched, skipPaymentCheck = false) {
    // Ignore a duplicate request while a step-forward is already in flight.
    if (stepForwardInFlightRef.current) return;

    const namedStep = getCurrentStep();
    // Clear any prior surfaced error when the user retries.
    setStepError(null);

    // Prevent automatic submission on payment step if no payment method exists
    // This prevents unwanted mutations when user hasn't entered payment details
    // Note: skipPaymentCheck should be true when called after successfully storing a payment method,
    // as the cache may be updated but React hasn't re-rendered yet, so data from closure is stale
    if (namedStep === IASessionSteps.ADD_CARD_DETAILS.name && !skipPaymentCheck) {
      const paymentMethod = usablePaymentMethod(
        data.insuranceApplicationSession?.contractor
      );
      if (!paymentMethod) {
        // Don't proceed if there's no payment method on the payment step
        // The user needs to either use existing payment method or add a new one
        // This prevents automatic form submissions that would fail with "Missing Checkout payment provider instrument id"
        console.warn("Cannot proceed from payment step without a payment method");
        return;
      }
    }
    
    const fieldErrors = {};
    const fieldTouched = {};

    // Claim the in-flight guard synchronously so a second call (e.g. the e-sign
    // auto-advance racing a manual click) is ignored. Released when the mutation
    // settles (onCompleted/onError), if validation fails, or if building the
    // validation chain throws synchronously (below)—so the guard can never be
    // left stuck, which would permanently disable Continue until remount.
    stepForwardInFlightRef.current = true;

    try {
      currentValidationSchema
        .validate(ref.current.values, { abortEarly: false })
        .then((validData) => {
          saveAndStepForward({
            variables: {
              currentStep: namedStep,
              iaSession: iaSession,
              formValues: ref.current.values,
            },
          });
        })
        .catch((validationErrors) => {
          stepForwardInFlightRef.current = false;
          validationErrors.inner.forEach((item) => {
            fieldErrors[item.path] = item.message;
            fieldTouched[item.path] = true;
          });
          setErrors(fieldErrors);
          setTouched(fieldErrors);
        });
    } catch (err) {
      // The validation chain threw synchronously before any promise was
      // created (e.g. the Formik ref was momentarily null). Release the guard
      // so Continue isn't permanently stuck.
      stepForwardInFlightRef.current = false;
      console.warn("stepForward could not start:", err);
    }
  }

  function rightColMinPadding(activeStep) {
    /*
    Note: Final review content is extra tall 
    so minimizing padding for that step. Reuse
    anywhere you need to minimize padding to fit
    extra tall content.
    */
    const step = filterObject(IASessionSteps, (val, key) => {
      return val["order"] === activeStep;
    });
    return !!step?.FINAL_REVIEW?.name;
  }

  const setInstance = (stepWizard) => {
    updateState({
      ...state,
      stepWizard,
    });
    // Sync activeStep with StepWizard's current step when instance is set
    if (stepWizard && stepWizard.currentStep) {
      setActiveStep(stepWizard.currentStep);
    }
  };

  function base64Decode(id) {
    let res = atob(id);
    return Number(res.toString("ascii").split(":")[1]);
  }

  const unredacted = JSON.parse(
    data.insuranceApplicationSession?.contractor?.unredactedDict
  );

  const companyName = data.insuranceApplicationSession?.contractor?.companyName;

  formInitialValues.firstName = unredacted.first_name;
  formInitialValues.middleName = unredacted.middle_name;
  formInitialValues.lastName = unredacted.last_name;
  formInitialValues.line1 = unredacted.address.line1;
  formInitialValues.line2 = unredacted.address.line2;
  formInitialValues.line3 = unredacted.address.line3;
  formInitialValues.locality = unredacted.address.locality;
  formInitialValues.region = unredacted.address.region;
  formInitialValues.postalcode = unredacted.address.postalcode;
  formInitialValues.hasBusinessEntity = unredacted.has_business_entity;
  formInitialValues.companyName = data.insuranceApplicationSession?.contractor?.companyName || "";
  // For a completed session the backend redacts `tax_identification` to `{}`
  // (no ssn_token/ein), so guard the nested access. These values are not used
  // post-completion anyway (ApplicationComplete renders before the form), but
  // the optional chain prevents a crash if `tax_identification` is ever absent.
  formInitialValues.ssnToken = unredacted.tax_identification?.ssn_token || "";
  formInitialValues.ein = unredacted.tax_identification?.ein || "";

  const paymentClientToken =
    data.insuranceApplicationSession?.contractor?.paymentClientToken;
  const paymentMethod = usablePaymentMethod(
    data.insuranceApplicationSession?.contractor
  );

  const session = data.insuranceApplicationSession;
  const isGeneralOptIn = session?.isGeneralOptIn === true;
  const quotes = session?.quotes?.edges;
  const firstQuote = quotes?.[0]?.node;
  
  const isEsignatureComplete = session?.isEsignatureComplete;
  const policies = session?.policies;
  const successUrl = session?.successUrl;
  const cancelUrl = session?.cancelUrl;
  const currentStepName = session?.currentStep;
  
  // For general opt-in sessions, organizationName comes directly from the session
  // For standard sessions, extract from quotes (fallback to session organizationName if available)
  const organizationName = session?.organizationName || firstQuote?.tenant?.organization?.name || null;
  const entityName = firstQuote?.job?.entity?.name || null;
  const orgId = firstQuote?.tenant?.organization?.publicId || null;

  // Render ApplicationComplete if:
  // 1. Policies exist (standard flow), OR
  // 2. For general opt-in sessions, if currentStep is APPLICATION_COMPLETE (policies might not exist yet)
  // For standard sessions, we require policies to exist before showing ApplicationComplete
  // Show the real completion screen only when a policy actually exists (a bound
  // standard/agency-pay session), or for general opt-in (which legitimately
  // completes with no policy). An orphaned non-opt-in session deliberately
  // falls through to the wizard, where isOrphanedComplete routes it to
  // FINAL_REVIEW for remediation instead of asserting coverage that isn't bound.
  const shouldShowApplicationComplete = (policies?.edges?.length > 0) ||
    (isGeneralOptIn && currentStepName === "APPLICATION_COMPLETE");

  if (shouldShowApplicationComplete) {
    return (
      <ApplicationComplete
        orgId={orgId}
        successUrl={successUrl}
        cancelUrl={cancelUrl}
        policies={policies}
        client={props.client}
        isGeneralOptIn={isGeneralOptIn}
      />
    );
  }

  if (eligible === false) {
    return <ManualReview data={data} />;
  }

  return (
    <div className="App">
      <header className="App-header">
        <article className="cf overflow-hidden-l">
          <Col>{_renderHeadline(activeStep, organizationName, entityName, data)}</Col>
          <RightCol>
            <Container minpadding={rightColMinPadding(activeStep)}>
              <input type="hidden" name="activeStep" value={activeStep} />
              {stepError && (
                <div
                  role="alert"
                  className="pa3 mb3 br2 dark-red bg-washed-red ba b--light-red"
                >
                  {stepError}
                </div>
              )}
              <Formik
                innerRef={ref}
                initialValues={formInitialValues}
                validationSchema={currentValidationSchema}
                onSubmit={_handleSubmit}
              >
                {(props) => {
                  const {
                    setFieldValue,
                    values,
                    errors,
                    setErrors,
                    touched,
                    setTouched,
                  } = props;

                  return (
                    <StepWizard
                      initialStep={currentStep}
                      isHashEnabled={false}
                      transitions={state.transitions}
                      instance={setInstance}
                    >
                      <VerifyForm
                        touched={touched}
                        errors={errors}
                        values={values}
                        stepBack={stepBack}
                        stepForward={stepForward}
                        stepForwardLoading={stepForwardLoading}
                        stepName={IASessionSteps.VERIFY_INFO.name}
                        formField={formField}
                        businessEntityChoice={
                          data.insuranceApplicationSession?.contractor
                            ?.hasBusinessEntity
                        }
                        setFieldValue={setFieldValue}
                        setErrors={setErrors}
                        setTouched={setTouched}
                      />
                      <ApplicationForm
                        stepBack={stepBack}
                        stepForwardLoading={stepForwardLoading}
                        stepBackLoading={stepBackLoading}
                        stepForward={stepForward}
                        stepName={IASessionSteps.APPLICATION_QUESTIONS.name}
                        formField={formField}
                        setFieldValue={setFieldValue}
                        values={values}
                      />
                      <ReviewQuoteForm
                        stepBack={stepBack}
                        stepForward={stepForward}
                        stepForwardLoading={stepForwardLoading}
                        stepName={IASessionSteps.ESIGNATURE_DOCUMENT.name}
                        formField={formField}
                        esignEmbedUrl={esignEmbedUrl}
                        esignProvider={esignProvider || "eversign"}
                        documentHash={esignDocumentHash}
                        isEsignatureComplete={isEsignatureComplete}
                        withholdPremium={data.insuranceApplicationSession?.contractor?.withholdPremium}
                        withholdPremiumOrgOverride={data.insuranceApplicationSession?.contractor?.withholdPremiumOrgOverride}
                        organizationName={organizationName}
                        isGeneralOptIn={isGeneralOptIn}
                      />
                      {!isGeneralOptIn && (
                        <PaymentForm
                          isSandbox={isSandbox}
                          paymentError={paymentError}
                          declineCount={declineCount}
                          reportPaymentFailure={reportPaymentFailure}
                          stepBack={stepBack}
                          stepForward={stepForward}
                          stepForwardLoading={stepForwardLoading}
                          stepName={IASessionSteps.ADD_CARD_DETAILS.name}
                          formField={formField}
                          paymentClientToken={paymentClientToken}
                          paymentMethod={paymentMethod}
                        />
                      )}
                      {!isGeneralOptIn && (
                        <FinalReview
                          stepName={IASessionSteps.FINAL_REVIEW.name}
                          withholdPremium={data.insuranceApplicationSession?.contractor?.withholdPremium}
                          withholdPremiumOrgOverride={data.insuranceApplicationSession?.contractor?.withholdPremiumOrgOverride}
                          isRemediation={isOrphanedComplete}
                          data={data}
                        />
                      )}
                    </StepWizard>
                  );
                }}
              </Formik>
            </Container>
          </RightCol>
        </article>
      </header>
    </div>
  );
};

export default Wizard;
